import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChildEducationAnswer,
  COUNTRY_DEFAULT_CURRENCY,
  HousingType,
  OnboardingAnswers,
  applyPrimaryCurrency,
  createEmptyCar,
  createEmptyChild,
  createEmptyIncomeSource,
  createEmptyLivingLocation,
  createEmptyProperty,
  createEmptyRentalUnit
} from '../utils/onboardingCategoryBuilder';
import {
  AmountCurrencySelect,
  CardRemoveButton,
  CountrySelect,
  RecurringFields,
  UtilityToggle,
  parseYears
} from './onboarding/OnboardingBuilderFields';
import { getAllCurrencies } from '../utils/shared';

interface OnboardingCategoryBuilderProps {
  answers: OnboardingAnswers;
  onChange: (answers: OnboardingAnswers) => void;
  incomeSectionRef?: React.RefObject<HTMLElement | null>;
}

const HOUSING_TYPES: HousingType[] = ['rent', 'mortgage', 'own'];

const INCOME_TYPES = ['salary', 'family', 'business', 'dividends', 'rental', 'other'] as const;

const OnboardingCategoryBuilder: React.FC<OnboardingCategoryBuilderProps> = ({
  answers,
  onChange,
  incomeSectionRef
}) => {
  const { t } = useTranslation();
  const defaultCurrency = answers.primaryCurrency || 'USD';
  const allCurrencies = getAllCurrencies();

  const update = (patch: Partial<OnboardingAnswers>) => {
    onChange({ ...answers, ...patch });
  };

  const setPrimaryCountry = (primaryCountry: string) => {
    const suggested = COUNTRY_DEFAULT_CURRENCY[primaryCountry] || defaultCurrency;
    const previous = answers.primaryCurrency || 'USD';
    let next = { ...answers, primaryCountry };
    if (suggested !== previous) {
      next = applyPrimaryCurrency(next, suggested, previous);
    } else {
      next = { ...next, primaryCurrency: previous };
    }
    onChange(next);
  };

  const setPrimaryCurrency = (primaryCurrency: string) => {
    const previous = answers.primaryCurrency || 'USD';
    onChange(applyPrimaryCurrency(answers, primaryCurrency, previous));
  };

  const lifestyleToggles = [
    ['includeShopping', 'builder.groups.shopping'],
    ['includeHealth', 'builder.groups.health'],
    ['includeGifts', 'builder.groups.gifts'],
    ['includeTravel', 'builder.groups.travel'],
    ['includeSubscriptions', 'builder.groups.subscriptions'],
    ['includeAssets', 'builder.groups.assets'],
    ['includeCharity', 'builder.groups.charity'],
    ['includeOther', 'builder.groups.other']
  ] as const;

  return (
    <div className="category-builder">
      <section className="builder-section builder-privacy">
        <h4>{t('builder.sections.privacy.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.privacy.help', { ns: 'onboarding' })}</p>
      </section>

      <section className="builder-section">
        <h4>{t('builder.sections.primary.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.primary.help', { ns: 'onboarding' })}</p>
        <div className="builder-recurring">
          <CountrySelect
            label={t('builder.sections.primary.countryLabel', { ns: 'onboarding' })}
            value={answers.primaryCountry}
            onChange={setPrimaryCountry}
          />
          <label className="builder-field">
            <span>{t('builder.sections.primary.currencyLabel', { ns: 'onboarding' })}</span>
            <select
              value={defaultCurrency}
              onChange={e => setPrimaryCurrency(e.target.value)}
            >
              {allCurrencies.map(currency => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* Income — start here */}
      <section
        ref={incomeSectionRef}
        id="onboarding-income-section"
        className="builder-section builder-section-income"
      >
        <h4>{t('builder.sections.income.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.income.help', { ns: 'onboarding' })}</p>
        {answers.incomeSources.map((source, index) => (
          <div key={source.id} className="builder-card">
            <div className="builder-card-header">
              <select
                className="builder-text-input"
                value={source.type}
                onChange={e => {
                  const incomeSources = [...answers.incomeSources];
                  incomeSources[index] = {
                    ...source,
                    type: e.target.value as typeof source.type
                  };
                  update({ incomeSources });
                }}
              >
                {INCOME_TYPES.map(type => (
                  <option key={type} value={type}>
                    {t(`builder.incomeTypes.${type}`, { ns: 'onboarding' })}
                  </option>
                ))}
              </select>
              <CardRemoveButton
                onClick={() =>
                  update({ incomeSources: answers.incomeSources.filter((_, i) => i !== index) })
                }
                label={t('builder.actions.remove', { ns: 'onboarding' })}
              />
            </div>
            <input
              type="text"
              className="builder-text-input"
              value={source.label}
              placeholder={t('builder.sections.income.labelPlaceholder', { ns: 'onboarding' })}
              onChange={e => {
                const incomeSources = [...answers.incomeSources];
                incomeSources[index] = { ...source, label: e.target.value };
                update({ incomeSources });
              }}
            />
            <label className="builder-field">
              <span>{t('builder.sections.income.expectedMonthly', { ns: 'onboarding' })}</span>
              <div className="builder-amount-input">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={source.expectedMonthly ?? ''}
                  placeholder="0"
                  onChange={e => {
                    const incomeSources = [...answers.incomeSources];
                    incomeSources[index] = {
                      ...source,
                      expectedMonthly: e.target.value ? Number(e.target.value) : null,
                      expectedCurrency: source.expectedCurrency || defaultCurrency
                    };
                    update({ incomeSources });
                  }}
                />
                <AmountCurrencySelect
                  value={source.expectedCurrency || defaultCurrency}
                  onChange={expectedCurrency => {
                    const incomeSources = [...answers.incomeSources];
                    incomeSources[index] = { ...source, expectedCurrency };
                    update({ incomeSources });
                  }}
                />
              </div>
            </label>
          </div>
        ))}
        <button
          type="button"
          className="builder-add"
          onClick={() =>
            update({
              incomeSources: [
                ...answers.incomeSources,
                createEmptyIncomeSource(answers.incomeSources.length, defaultCurrency)
              ]
            })
          }
        >
          + {t('builder.sections.income.add', { ns: 'onboarding' })}
        </button>
      </section>

      {/* Rental units */}
      <section className="builder-section">
        <h4>{t('builder.sections.rentals.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.rentals.help', { ns: 'onboarding' })}</p>
        {answers.rentalUnits.map((unit, index) => (
          <div key={unit.id} className="builder-card">
            <div className="builder-card-header">
              <input
                type="text"
                className="builder-text-input"
                value={unit.label}
                placeholder={t('builder.sections.rentals.labelPlaceholder', {
                  ns: 'onboarding',
                  number: index + 1
                })}
                onChange={e => {
                  const rentalUnits = [...answers.rentalUnits];
                  rentalUnits[index] = { ...unit, label: e.target.value };
                  update({ rentalUnits });
                }}
              />
              <CardRemoveButton
                onClick={() =>
                  update({ rentalUnits: answers.rentalUnits.filter((_, i) => i !== index) })
                }
                label={t('builder.actions.remove', { ns: 'onboarding' })}
              />
            </div>
            <div className="builder-recurring">
              <CountrySelect
                label={t('builder.fields.country', { ns: 'onboarding' })}
                value={unit.country}
                onChange={country => {
                  const rentalUnits = [...answers.rentalUnits];
                  rentalUnits[index] = { ...unit, country };
                  update({ rentalUnits });
                }}
              />
              <label className="builder-field">
                <span>{t('builder.sections.rentals.housingType', { ns: 'onboarding' })}</span>
                <select
                  value={unit.housingType}
                  onChange={e => {
                    const rentalUnits = [...answers.rentalUnits];
                    rentalUnits[index] = {
                      ...unit,
                      housingType: e.target.value as HousingType
                    };
                    update({ rentalUnits });
                  }}
                >
                  {HOUSING_TYPES.map(type => (
                    <option key={type} value={type}>
                      {t(`builder.housing.${type}`, { ns: 'onboarding' })}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {unit.housingType !== 'own' && (
              <RecurringFields
                bill={unit.payment}
                onChange={payment => {
                  const rentalUnits = [...answers.rentalUnits];
                  rentalUnits[index] = { ...unit, payment };
                  update({ rentalUnits });
                }}
                amountLabel={
                  unit.housingType === 'mortgage'
                    ? t('builder.sections.rentals.mortgageAmount', { ns: 'onboarding' })
                    : t('builder.sections.rentals.rentAmount', { ns: 'onboarding' })
                }
              defaultCurrency={defaultCurrency}
            />
            )}
          </div>
        ))}
        <button
          type="button"
          className="builder-add"
          onClick={() =>
            update({
              rentalUnits: [...answers.rentalUnits, createEmptyRentalUnit(answers.rentalUnits.length, defaultCurrency)]
            })
          }
        >
          + {t('builder.sections.rentals.add', { ns: 'onboarding' })}
        </button>
      </section>

      {/* Living locations */}
      <section className="builder-section">
        <h4>{t('builder.sections.locations.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.locations.help', { ns: 'onboarding' })}</p>
        {answers.livingLocations.map((location, index) => (
          <div key={location.id} className="builder-card">
            <div className="builder-card-header">
              <input
                type="text"
                className="builder-text-input"
                value={location.label}
                placeholder={t('builder.sections.locations.labelPlaceholder', {
                  ns: 'onboarding',
                  number: index + 1
                })}
                onChange={e => {
                  const livingLocations = [...answers.livingLocations];
                  livingLocations[index] = { ...location, label: e.target.value };
                  update({ livingLocations });
                }}
              />
              <CardRemoveButton
                onClick={() =>
                  update({
                    livingLocations: answers.livingLocations.filter((_, i) => i !== index)
                  })
                }
                label={t('builder.actions.remove', { ns: 'onboarding' })}
              />
            </div>
            <CountrySelect
              label={t('builder.fields.country', { ns: 'onboarding' })}
              value={location.country}
              onChange={country => {
                const livingLocations = [...answers.livingLocations];
                livingLocations[index] = { ...location, country };
                update({ livingLocations });
              }}
            />
            <p className="builder-sublabel">{t('builder.sections.locations.spending', { ns: 'onboarding' })}</p>
            <RecurringFields
              bill={location.groceries}
              onChange={groceries => {
                const livingLocations = [...answers.livingLocations];
                livingLocations[index] = { ...location, groceries };
                update({ livingLocations });
              }}
              amountLabel={t('builder.items.groceries', { ns: 'onboarding' })}
              defaultCurrency={defaultCurrency}
            />
            <RecurringFields
              bill={location.transportation}
              onChange={transportation => {
                const livingLocations = [...answers.livingLocations];
                livingLocations[index] = { ...location, transportation };
                update({ livingLocations });
              }}
              amountLabel={t('builder.items.transportation', { ns: 'onboarding' })}
              defaultCurrency={defaultCurrency}
            />
            <p className="builder-sublabel">{t('builder.sections.utilities.title', { ns: 'onboarding' })}</p>
            <UtilityToggle
              label={t('builder.items.electricity', { ns: 'onboarding' })}
              enabled={location.electricity.enabled}
              bill={location.electricity.bill}
              onChange={(enabled, bill) => {
                const livingLocations = [...answers.livingLocations];
                livingLocations[index] = { ...location, electricity: { enabled, bill } };
                update({ livingLocations });
              }}
              defaultCurrency={defaultCurrency}
            />
            <UtilityToggle
              label={t('builder.items.water', { ns: 'onboarding' })}
              enabled={location.water.enabled}
              bill={location.water.bill}
              onChange={(enabled, bill) => {
                const livingLocations = [...answers.livingLocations];
                livingLocations[index] = { ...location, water: { enabled, bill } };
                update({ livingLocations });
              }}
              defaultCurrency={defaultCurrency}
            />
            <UtilityToggle
              label={t('builder.items.internet', { ns: 'onboarding' })}
              enabled={location.internet.enabled}
              bill={location.internet.bill}
              onChange={(enabled, bill) => {
                const livingLocations = [...answers.livingLocations];
                livingLocations[index] = { ...location, internet: { enabled, bill } };
                update({ livingLocations });
              }}
              defaultCurrency={defaultCurrency}
            />
            <UtilityToggle
              label={t('builder.items.phone', { ns: 'onboarding' })}
              enabled={location.phone.enabled}
              bill={location.phone.bill}
              onChange={(enabled, bill) => {
                const livingLocations = [...answers.livingLocations];
                livingLocations[index] = { ...location, phone: { enabled, bill } };
                update({ livingLocations });
              }}
              defaultCurrency={defaultCurrency}
            />
          </div>
        ))}
        <button
          type="button"
          className="builder-add"
          onClick={() =>
            update({
              livingLocations: [
                ...answers.livingLocations,
                createEmptyLivingLocation(answers.livingLocations.length, defaultCurrency)
              ]
            })
          }
        >
          + {t('builder.sections.locations.add', { ns: 'onboarding' })}
        </button>
      </section>

      {/* Cars */}
      <section className="builder-section">
        <h4>{t('builder.sections.cars.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.cars.help', { ns: 'onboarding' })}</p>
        {answers.cars.map((car, index) => (
          <div key={car.id} className="builder-card">
            <div className="builder-card-header">
              <input
                type="text"
                className="builder-text-input"
                value={car.name}
                placeholder={t('builder.sections.cars.namePlaceholder', {
                  ns: 'onboarding',
                  number: index + 1
                })}
                onChange={e => {
                  const cars = [...answers.cars];
                  cars[index] = { ...car, name: e.target.value };
                  update({ cars });
                }}
              />
              <CardRemoveButton
                onClick={() => update({ cars: answers.cars.filter((_, i) => i !== index) })}
                label={t('builder.actions.remove', { ns: 'onboarding' })}
              />
            </div>
            <CountrySelect
              label={t('builder.sections.cars.countryHint', { ns: 'onboarding' })}
              value={car.country}
              onChange={country => {
                const cars = [...answers.cars];
                cars[index] = { ...car, country };
                update({ cars });
              }}
            />
            <p className="builder-sublabel">{t('builder.sections.cars.fuel', { ns: 'onboarding' })}</p>
            <RecurringFields
              bill={car.fuel}
              onChange={fuel => {
                const cars = [...answers.cars];
                cars[index] = { ...car, fuel };
                update({ cars });
              }}
              defaultCurrency={defaultCurrency}
            />
            <p className="builder-sublabel">{t('builder.sections.cars.insurance', { ns: 'onboarding' })}</p>
            <RecurringFields
              bill={car.insurance}
              onChange={insurance => {
                const cars = [...answers.cars];
                cars[index] = { ...car, insurance };
                update({ cars });
              }}
              defaultCurrency={defaultCurrency}
            />
            <p className="builder-sublabel">{t('builder.sections.cars.maintenance', { ns: 'onboarding' })}</p>
            <RecurringFields
              bill={car.maintenance}
              onChange={maintenance => {
                const cars = [...answers.cars];
                cars[index] = { ...car, maintenance };
                update({ cars });
              }}
              defaultCurrency={defaultCurrency}
            />
            <p className="builder-sublabel">{t('builder.sections.cars.loan', { ns: 'onboarding' })}</p>
            <RecurringFields
              bill={car.loan}
              onChange={loan => {
                const cars = [...answers.cars];
                cars[index] = { ...car, loan };
                update({ cars });
              }}
              defaultCurrency={defaultCurrency}
            />
          </div>
        ))}
        <button
          type="button"
          className="builder-add"
          onClick={() => update({ cars: [...answers.cars, createEmptyCar(answers.cars.length, defaultCurrency)] })}
        >
          + {t('builder.sections.cars.add', { ns: 'onboarding' })}
        </button>
      </section>

      {/* Children & education */}
      <section className="builder-section">
        <h4>{t('builder.sections.children.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.children.help', { ns: 'onboarding' })}</p>
        {answers.children.map((child, index) => (
          <ChildEducationCard
            key={child.id}
            child={child}
            index={index}
            defaultCurrency={defaultCurrency}
            onChange={updated => {
              const children = [...answers.children];
              children[index] = updated;
              update({ children });
            }}
            onRemove={() => update({ children: answers.children.filter((_, i) => i !== index) })}
          />
        ))}
        <button
          type="button"
          className="builder-add"
          onClick={() =>
            update({ children: [...answers.children, createEmptyChild(answers.children.length, defaultCurrency)] })
          }
        >
          + {t('builder.sections.children.add', { ns: 'onboarding' })}
        </button>
      </section>

      {/* Real estate investments */}
      <section className="builder-section">
        <h4>{t('builder.sections.properties.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.properties.help', { ns: 'onboarding' })}</p>
        {answers.properties.map((property, index) => (
          <div key={property.id} className="builder-card">
            <div className="builder-card-header">
              <input
                type="text"
                className="builder-text-input"
                value={property.name}
                placeholder={t('builder.sections.properties.namePlaceholder', {
                  ns: 'onboarding',
                  number: index + 1
                })}
                onChange={e => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, name: e.target.value };
                  update({ properties });
                }}
              />
              <CardRemoveButton
                onClick={() =>
                  update({ properties: answers.properties.filter((_, i) => i !== index) })
                }
                label={t('builder.actions.remove', { ns: 'onboarding' })}
              />
            </div>
            <CountrySelect
              label={t('builder.fields.country', { ns: 'onboarding' })}
              value={property.country}
              onChange={country => {
                const properties = [...answers.properties];
                properties[index] = { ...property, country };
                update({ properties });
              }}
            />
            <label className="builder-check">
              <input
                type="checkbox"
                checked={property.hasInstallments}
                onChange={e => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, hasInstallments: e.target.checked };
                  update({ properties });
                }}
              />
              <span>{t('builder.sections.properties.installments', { ns: 'onboarding' })}</span>
            </label>
            {property.hasInstallments && (
              <RecurringFields
                bill={property.installments}
                onChange={installments => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, installments };
                  update({ properties });
                }}
              defaultCurrency={defaultCurrency}
            />
            )}
            <label className="builder-check">
              <input
                type="checkbox"
                checked={property.hasMaintenance}
                onChange={e => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, hasMaintenance: e.target.checked };
                  update({ properties });
                }}
              />
              <span>{t('builder.sections.properties.maintenance', { ns: 'onboarding' })}</span>
            </label>
            {property.hasMaintenance && (
              <RecurringFields
                bill={property.maintenance}
                onChange={maintenance => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, maintenance };
                  update({ properties });
                }}
              defaultCurrency={defaultCurrency}
            />
            )}
            <label className="builder-check">
              <input
                type="checkbox"
                checked={property.hasPropertyTax}
                onChange={e => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, hasPropertyTax: e.target.checked };
                  update({ properties });
                }}
              />
              <span>{t('builder.sections.properties.propertyTax', { ns: 'onboarding' })}</span>
            </label>
            {property.hasPropertyTax && (
              <RecurringFields
                bill={property.propertyTax}
                onChange={propertyTax => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, propertyTax };
                  update({ properties });
                }}
              defaultCurrency={defaultCurrency}
            />
            )}
            <label className="builder-check">
              <input
                type="checkbox"
                checked={property.hasInsurance}
                onChange={e => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, hasInsurance: e.target.checked };
                  update({ properties });
                }}
              />
              <span>{t('builder.sections.properties.insurance', { ns: 'onboarding' })}</span>
            </label>
            {property.hasInsurance && (
              <RecurringFields
                bill={property.insurance}
                onChange={insurance => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, insurance };
                  update({ properties });
                }}
              defaultCurrency={defaultCurrency}
            />
            )}
            <label className="builder-check">
              <input
                type="checkbox"
                checked={property.rentsOut}
                onChange={e => {
                  const properties = [...answers.properties];
                  properties[index] = { ...property, rentsOut: e.target.checked };
                  update({ properties });
                }}
              />
              <span>{t('builder.sections.properties.rentsOut', { ns: 'onboarding' })}</span>
            </label>
            {property.rentsOut && (
              <>
                <RecurringFields
                  bill={property.rent}
                  onChange={rent => {
                    const properties = [...answers.properties];
                    properties[index] = { ...property, rent };
                    update({ properties });
                  }}
                  amountLabel={t('builder.sections.properties.rentIncome', { ns: 'onboarding' })}
              defaultCurrency={defaultCurrency}
            />
                <RecurringFields
                  bill={property.rentalExpenses}
                  onChange={rentalExpenses => {
                    const properties = [...answers.properties];
                    properties[index] = { ...property, rentalExpenses };
                    update({ properties });
                  }}
                  amountLabel={t('builder.sections.properties.rentalExpenses', { ns: 'onboarding' })}
              defaultCurrency={defaultCurrency}
            />
              </>
            )}
          </div>
        ))}
        <button
          type="button"
          className="builder-add"
          onClick={() =>
            update({
              properties: [...answers.properties, createEmptyProperty(answers.properties.length, defaultCurrency)]
            })
          }
        >
          + {t('builder.sections.properties.add', { ns: 'onboarding' })}
        </button>
      </section>

      {/* Lifestyle */}
      <section className="builder-section">
        <h4>{t('builder.sections.lifestyle.title', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('builder.sections.lifestyle.help', { ns: 'onboarding' })}</p>
        {lifestyleToggles.map(([key, labelKey]) => (
          <label key={key} className="builder-check">
            <input
              type="checkbox"
              checked={answers[key]}
              onChange={e => update({ [key]: e.target.checked })}
            />
            <span>{t(labelKey, { ns: 'onboarding' })}</span>
          </label>
        ))}
        {answers.includeSubscriptions && (
          <>
            <p className="builder-sublabel">
              {t('builder.sections.lifestyle.subscriptionAmount', { ns: 'onboarding' })}
            </p>
            <RecurringFields
              bill={answers.subscriptionBill}
              onChange={subscriptionBill => update({ subscriptionBill })}
              defaultCurrency={defaultCurrency}
            />
          </>
        )}
      </section>
    </div>
  );
};

const ChildEducationCard: React.FC<{
  child: ChildEducationAnswer;
  index: number;
  onChange: (child: ChildEducationAnswer) => void;
  onRemove: () => void;
  defaultCurrency?: string;
}> = ({ child, index, onChange, onRemove, defaultCurrency = 'USD' }) => {
  const { t } = useTranslation();

  return (
    <div className="builder-card">
      <div className="builder-card-header">
        <input
          type="text"
          className="builder-text-input"
          value={child.name}
          placeholder={t('builder.sections.children.namePlaceholder', {
            ns: 'onboarding',
            number: index + 1
          })}
          onChange={e => onChange({ ...child, name: e.target.value })}
        />
        <CardRemoveButton onClick={onRemove} label={t('builder.actions.remove', { ns: 'onboarding' })} />
      </div>

      <p className="builder-sublabel">{t('builder.sections.children.schooling', { ns: 'onboarding' })}</p>
      <div className="builder-recurring">
        <div className="builder-recurring-meta">
          <label className="builder-field">
            <span>{t('builder.sections.children.yearsLeft', { ns: 'onboarding' })}</span>
            <input
              type="number"
              min="0"
              max="20"
              value={child.yearsOfSchoolLeft ?? ''}
              onChange={e =>
                onChange({ ...child, yearsOfSchoolLeft: parseYears(e.target.value) })
              }
            />
          </label>
          <label className="builder-field">
            <span>{t('builder.sections.children.endMonth', { ns: 'onboarding' })}</span>
            <input
              type="number"
              min="1"
              max="12"
              value={child.schoolEndMonth ?? ''}
              onChange={e =>
                onChange({
                  ...child,
                  schoolEndMonth: e.target.value ? Number(e.target.value) : null
                })
              }
            />
          </label>
        </div>
        <label className="builder-field builder-field-amount">
          <span>{t('builder.sections.children.avgYearly', { ns: 'onboarding' })}</span>
          <div className="builder-amount-input">
            <input
              type="number"
              min="0"
              step="0.01"
              value={child.avgSchoolYearly ?? ''}
              onChange={e =>
                onChange({
                  ...child,
                  avgSchoolYearly: e.target.value ? Number(e.target.value) : null,
                  avgSchoolCurrency: child.avgSchoolCurrency || defaultCurrency
                })
              }
            />
            <AmountCurrencySelect
              value={child.avgSchoolCurrency || defaultCurrency}
              onChange={avgSchoolCurrency => onChange({ ...child, avgSchoolCurrency })}
            />
          </div>
        </label>
      </div>

      <p className="builder-sublabel">{t('builder.sections.children.university', { ns: 'onboarding' })}</p>
      <div className="builder-recurring">
        <div className="builder-recurring-meta">
          <label className="builder-field">
            <span>{t('builder.sections.children.yearsLeft', { ns: 'onboarding' })}</span>
            <input
              type="number"
              min="0"
              max="10"
              value={child.yearsOfUniversityLeft ?? ''}
              onChange={e =>
                onChange({ ...child, yearsOfUniversityLeft: parseYears(e.target.value) })
              }
            />
          </label>
          <label className="builder-field">
            <span>{t('builder.sections.children.endMonth', { ns: 'onboarding' })}</span>
            <input
              type="number"
              min="1"
              max="12"
              value={child.universityEndMonth ?? ''}
              onChange={e =>
                onChange({
                  ...child,
                  universityEndMonth: e.target.value ? Number(e.target.value) : null
                })
              }
            />
          </label>
        </div>
        <label className="builder-field builder-field-amount">
          <span>{t('builder.sections.children.avgYearly', { ns: 'onboarding' })}</span>
          <div className="builder-amount-input">
            <input
              type="number"
              min="0"
              step="0.01"
              value={child.avgUniversityYearly ?? ''}
              onChange={e =>
                onChange({
                  ...child,
                  avgUniversityYearly: e.target.value ? Number(e.target.value) : null,
                  avgUniversityCurrency: child.avgUniversityCurrency || defaultCurrency
                })
              }
            />
            <AmountCurrencySelect
              value={child.avgUniversityCurrency || defaultCurrency}
              onChange={avgUniversityCurrency => onChange({ ...child, avgUniversityCurrency })}
            />
          </div>
        </label>
      </div>

      <RecurringFields
        bill={child.supplies}
        onChange={supplies => onChange({ ...child, supplies })}
        amountLabel={t('builder.items.schoolSupplies', { ns: 'onboarding' })}
              defaultCurrency={defaultCurrency}
            />
      <RecurringFields
        bill={child.activities}
        onChange={activities => onChange({ ...child, activities })}
        amountLabel={t('builder.items.schoolActivities', { ns: 'onboarding' })}
              defaultCurrency={defaultCurrency}
            />
    </div>
  );
};

export default OnboardingCategoryBuilder;
